/**
 * Densité de l'interface : UN identifiant choisi par l'exploitant dans une liste
 * fermée, traduit ici en trois familles de valeurs :
 *
 *   - l'échelle d'espacement `--space-*` (marges, rembourrages, écarts) ;
 *   - la hauteur des contrôles `--control-h*` (boutons, champs), lue par
 *     `assets/main.css` avec sa valeur actuelle en repli ;
 *   - la hauteur des lignes des tableaux virtualisés, un NOMBRE : la
 *     virtualisation calcule ses positions en JavaScript, une variable CSS ne
 *     lui servirait à rien (voir `densiteAppliquee.ts`).
 *
 * Fonctions PURES, comme `boutons.ts` — l'application au document vit dans
 * `appliquer.ts`. Aucune valeur reçue n'entre dans la feuille : seul
 * l'identifiant voyage, les valeurs sont écrites dans ce fichier.
 */

/**
 * Niveaux proposés. MIROIR de `NIVEAUX_DENSITE` (backend
 * `core/apparence/schemas.ts`) et de la migration 0045 — un test backend fige
 * les trois.
 */
export const NIVEAUX_DENSITE = ['compacte', 'normale', 'aeree'] as const
export type NiveauDensite = (typeof NIVEAUX_DENSITE)[number]

export interface DensitePlateforme {
  level?: NiveauDensite
}

/** La densité de `main.css` : celle qu'on obtient sans aucune variable déclarée. */
export const DEFAUT_DENSITE: NiveauDensite = 'normale'

/**
 * Échelle d'espacement de `main.css` (grille de 4 px), par palier. Un test
 * vérifie qu'elle correspond EXACTEMENT aux déclarations de la feuille : un
 * palier oublié ici ne suivrait pas la densité, un palier faux la décalerait.
 * `--space-0` (0) n'a rien à mettre à l'échelle.
 */
export const ESPACES_BASE = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
} as const

/**
 * Multiplicateur de l'échelle. Arrondi au pixel : un rembourrage de 4,5 px
 * tombe entre deux pixels physiques et rend un bord flou sur un écran standard.
 */
export const FACTEURS_ESPACE: Readonly<Record<NiveauDensite, number>> = {
  compacte: 0.75,
  normale: 1,
  aeree: 1.25,
}

/** Variables de hauteur lues par `main.css`, avec ces valeurs « normale » en repli. */
export const VARIABLES_CONTROLES = ['--control-h', '--control-h-sm', '--control-h-lg'] as const
export type VariableControle = (typeof VARIABLES_CONTROLES)[number]

export const HAUTEURS_CONTROLES: Readonly<
  Record<NiveauDensite, Readonly<Record<VariableControle, number>>>
> = {
  compacte: { '--control-h': 28, '--control-h-sm': 24, '--control-h-lg': 36 },
  normale: { '--control-h': 32, '--control-h-sm': 28, '--control-h-lg': 40 },
  aeree: { '--control-h': 36, '--control-h-sm': 32, '--control-h-lg': 44 },
}

/** Densité PROPRE à un tableau (prop `density` des tableaux virtualisés). */
export type DensiteTableau = 'comfortable' | 'compact'

/**
 * Hauteur de ligne des tableaux virtualisés. La colonne « normale » reprend les
 * valeurs historiques des composants (44 et 36) : sans réglage, rien ne bouge.
 */
export const HAUTEURS_LIGNES: Readonly<
  Record<NiveauDensite, Readonly<Record<DensiteTableau, number>>>
> = {
  compacte: { comfortable: 36, compact: 32 },
  normale: { comfortable: 44, compact: 36 },
  aeree: { comfortable: 52, compact: 44 },
}

export function estNiveauDensite(valeur: unknown): valeur is NiveauDensite {
  return typeof valeur === 'string' && (NIVEAUX_DENSITE as readonly string[]).includes(valeur)
}

/** Valide une forme reçue et la recopie, ou `null` si UN élément est hors contrat. */
export function normaliserDensite(brut: unknown): DensitePlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: DensitePlateforme = {}
  for (const [cle, valeur] of Object.entries(brut)) {
    if (cle !== 'level' || !estNiveauDensite(valeur)) return null
    resultat.level = valeur
  }
  return resultat
}

function verifier(niveau: NiveauDensite): void {
  if (!estNiveauDensite(niveau)) throw new Error(`Densité inconnue : ${JSON.stringify(niveau)}`)
}

/**
 * Toutes les variables d'un niveau, valeurs EXPLICITES — y compris pour
 * « normale ». Pour l'aperçu, posé en ligne sur son cadre : il doit montrer le
 * niveau du brouillon même quand le document en applique un autre. Contrairement
 * aux boutons, `initial` n'est pas possible ici : `var(--space-4)` est lu SANS
 * repli dans toute l'application, une variable `initial` y vaudrait zéro.
 */
export function jetonsDensite(niveau: NiveauDensite): Record<string, string> {
  verifier(niveau)
  const jetons: Record<string, string> = {}
  for (const [palier, base] of Object.entries(ESPACES_BASE)) {
    jetons[`--space-${palier}`] = `${Math.round(base * FACTEURS_ESPACE[niveau])}px`
  }
  for (const nom of VARIABLES_CONTROLES) jetons[nom] = `${HAUTEURS_CONTROLES[niveau][nom]}px`
  return jetons
}

/** Hauteur de ligne d'un tableau virtualisé, pour un niveau donné. */
export function hauteurLigne(niveau: NiveauDensite, densiteTableau: DensiteTableau): number {
  verifier(niveau)
  return HAUTEURS_LIGNES[niveau][densiteTableau]
}

/**
 * Déclarées sur la racine : `--space-*` y est déclaré par `main.css`, et lu sans
 * repli partout. Spécificité (0,1,1), au-dessus du `:root` (0,1,0) de la
 * feuille. Le contraste renforcé n'est PAS exclu, contrairement aux couleurs et
 * aux boutons : la densité ne touche à aucune couleur.
 */
export const SELECTEUR_DENSITE = 'html:root'

/** Feuille du niveau, `''` pour le niveau par défaut. Lève sur un niveau inconnu. */
export function feuilleDensite(densite: DensitePlateforme): string {
  const niveau = densite.level
  if (niveau === undefined) return ''
  verifier(niveau)
  if (niveau === DEFAUT_DENSITE) return ''
  const lignes = Object.entries(jetonsDensite(niveau)).map(([n, v]) => `  ${n}: ${v};`)
  return `${SELECTEUR_DENSITE} {\n${lignes.join('\n')}\n}`
}
